import React, { type ReactNode, useContext, useState } from 'react';

import {
  type CreateSubIdeaEventLog,
  type FieldChangeEventLog,
  type IdeaEventLog,
  IdeaEventLogType,
  type MergeEventLog,
} from 'common/api/endpoints/ideaActivity';
import UserAvatarV2 from 'common/common/UserAvatarV2';
import { DefaultFieldNames, IdeaFieldTypes } from 'common/constants/ideas';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import IdeaStatusPill from 'common/ideas/IdeaStatusPill';
import LazyLoadedImage from 'common/LazyLoadedImage';
import { useIdeasContext } from 'common/subdomain/admin/v2/containers/IdeasContainer';
import { useUnmergeIdea } from 'common/subdomain/admin/v2/hooks/useIdeaMutations';
import Timestamp from 'common/Timestamp';
import truncateString from 'common/truncateString';
import AlertDialog from 'common/uiV3/AlertDialog';
import Avatar from 'common/uiV3/Avatar';
import Button from 'common/uiV3/Button';
import ButtonLink from 'common/uiV3/Button/ButtonLink';
import Flex from 'common/uiV3/Flex';
import capitalizeFirstLetter from 'common/util/capitalizeFirstLetter';
import { dayjs } from 'common/util/dayjsUtils';

import CannyIcon from 'img/canny-icon-white.svg';

import type { QueryParams as IdeaActivityQueryParams } from 'common/actions/ideaActivityQueries';
import type { StrippedUser } from 'common/api/endpoints/users';
import type { IdeaStatus } from 'common/api/resources/statuses';

type IdeaEventLogProps = {
  eventLog: IdeaEventLog;
  activityQueryParams: IdeaActivityQueryParams;
};

const EventLog = ({ eventLog, activityQueryParams }: IdeaEventLogProps) => {
  if (eventLog.eventType === IdeaEventLogType.createSubIdea) {
    return <IdeaCreateSubIdeaEventLog eventLog={eventLog} />;
  }

  if (eventLog.eventType === IdeaEventLogType.merge) {
    return <IdeaMergeEventLog eventLog={eventLog} activityQueryParams={activityQueryParams} />;
  }

  return <IdeaFieldChangeEventLog eventLog={eventLog} />;
};

const IdeaFieldChangeEventLog = ({ eventLog }: { eventLog: FieldChangeEventLog }) => {
  const { fieldName, newValue, oldValue } = eventLog.payload;

  const getLogMessage = () => {
    if (fieldName === DefaultFieldNames.Author) {
      return `created this idea`;
    }

    if (fieldName === DefaultFieldNames.Status) {
      return (
        <span className="inline-flex flex-wrap items-center gap-1 text-sm">
          <span>changed the status from</span>
          <IdeaStatusPill status={oldValue as IdeaStatus} />
          <span>to</span>
          <IdeaStatusPill status={newValue as IdeaStatus} />
        </span>
      );
    }

    return `set ${capitalizeFirstLetter(fieldName ?? '')} to ${formatLogValue(eventLog)}`;
  };

  return (
    <EventLogMessage
      message={getLogMessage()}
      changer={eventLog.changer}
      created={eventLog.created}
    />
  );
};

const IdeaCreateSubIdeaEventLog = ({ eventLog }: { eventLog: CreateSubIdeaEventLog }) => {
  return (
    <EventLogMessage
      message={`created sub-idea "${eventLog.payload.subIdeaTitle}"`}
      changer={eventLog.changer}
      created={eventLog.created}
    />
  );
};

const IdeaMergeEventLog = ({
  eventLog,
  activityQueryParams,
}: {
  eventLog: MergeEventLog;
  activityQueryParams: IdeaActivityQueryParams;
}) => {
  const showToast = useContext(ShowToastContext);
  const { removeIdeaActivities } = useIdeasContext();

  const [open, setOpen] = useState(false);

  const unmergeIdea = useUnmergeIdea({
    onSuccess: () => {
      setOpen(false);
      removeIdeaActivities(activityQueryParams, [eventLog._id]);
      showToast('Idea unmerged successfully', ToastTypes.success);
    },
  });

  return (
    <>
      <EventLogMessage
        message={`merged "${eventLog.payload.mergedFromIdeaTitle}" into this idea`}
        changer={eventLog.changer}
        created={eventLog.created}
        action={{
          title: 'Unmerge',
          onClick: () => setOpen(true),
        }}
      />
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialog.Content>
          <AlertDialog.Header>
            <AlertDialog.Title>Unmerge idea</AlertDialog.Title>
            <AlertDialog.Description>
              Are you sure you want to unmerge "{eventLog.payload.mergedFromIdeaTitle}" from this
              idea?
            </AlertDialog.Description>
          </AlertDialog.Header>
          <AlertDialog.Footer>
            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <Button
                onClick={() =>
                  unmergeIdea.mutate({
                    ideaID: eventLog.payload.mergedFromIdeaID,
                  })
                }
                isLoading={unmergeIdea.isLoading}>
                Yes, unmerge
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  );
};

const EventLogMessage = ({
  action,
  message,
  changer,
  created,
}: {
  changer: StrippedUser | null;
  created: string;
  message: ReactNode;
  action?: {
    title: string;
    onClick: () => void;
  };
}) => {
  return (
    <Flex className="items-start gap-2">
      {changer ? (
        <UserAvatarV2 user={changer} size={20} />
      ) : (
        <Avatar size={20}>
          <Avatar.Fallback>
            <Flex className="size-5 bg-primary rounded-full items-center justify-center">
              <LazyLoadedImage src={CannyIcon} alt="Canny" className="size-3" />
            </Flex>
          </Avatar.Fallback>
        </Avatar>
      )}
      <span className="space-x-1 text-secondary-foreground text-sm">
        {changer ? (
          <ButtonLink
            variant="link"
            color="secondary"
            className="text-sm no-underline!"
            to={`/admin/users/${changer.urlName}`}>
            {changer.name}
          </ButtonLink>
        ) : (
          <span>Canny</span>
        )}
        <span>{message}</span>
        <span>·</span>
        <span>
          <Timestamp inline timestamp={created} />
        </span>
        {action ? (
          <>
            <span>·</span>
            <Button
              variant="link"
              className="text-secondary-foreground hover:text-secondary-foreground text-sm"
              onClick={action.onClick}>
              {action.title}
            </Button>
          </>
        ) : null}
      </span>
    </Flex>
  );
};

const formatLogValue = (eventLog: FieldChangeEventLog) => {
  const { fieldType, newValue } = eventLog.payload;

  if (fieldType === IdeaFieldTypes.date) {
    if (typeof newValue === 'string' && dayjs(newValue).isValid()) {
      return dayjs(newValue).format('MMM D, YYYY');
    }
  }

  if (fieldType === IdeaFieldTypes.user) {
    return newValue?.name;
  }

  if (fieldType === IdeaFieldTypes.boolean) {
    return newValue ? 'Checked' : 'Unchecked';
  }

  if (fieldType === IdeaFieldTypes.multiselect) {
    return newValue?.join(', ');
  }

  if (typeof newValue === 'object') {
    return newValue?.name ?? 'Unknown';
  }

  if (typeof newValue === 'string') {
    return truncateString(newValue, 100);
  }

  return newValue;
};

export default EventLog;
